import React, {useEffect, useCallback, useMemo, useRef} from 'react'
import PropTypes from 'prop-types'
import {useLocation} from 'react-router-dom'
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useSiteCustomPreferences} from '../../../../hooks/use-site-custom-preferences'
import {sanitizeInput} from '../../../../utils/custom-utils'

export const GTMContext = React.createContext()

export const GTMProvider = ({gtmContext, children}) => {
    const {locale} = useMultiSite()
    const {data: customer} = useCurrentCustomer()
    const location = useLocation()
    const appOrigin = useAppOrigin()
    const config = getConfig()
    const {getcustomPreferencevalue} = useSiteCustomPreferences()
    const gtmEnabled = getcustomPreferencevalue('pwaGtmEnabled')
    const gtmScript = getcustomPreferencevalue('pwaGtmScript')
    const email = customer?.email ?? ''
    const registered = customer?.isRegistered
    const demandwareID = customer?.customerId
    const loggedInState = customer?.isRegistered
    const user_id = customer?.customerNo ?? ''
    const environment = config?.app?.library?.instanceName === 'PROD' ? 'production' : 'development'

    // Cache for the script content
    const scriptCache = useRef({content: null, isLoaded: false})

    const loadGtmScript = useCallback(() => {
        if (!gtmScript || scriptCache.current.content === gtmScript) {
            return
        }

        const existingScript = document.getElementById('gtm-script')
        if (existingScript) existingScript.remove()

        const parser = new DOMParser()
        const doc = parser.parseFromString(gtmScript, 'text/html')
        const scripts = doc.querySelectorAll('script')
        scripts.forEach((script) => {
            const newScript = document.createElement('script')
            newScript.id = 'gtm-script'
            if (script.src) {
                newScript.src = script.src
                newScript.async = script.async
            } else {
                newScript.textContent = script.textContent
            }

            document.body.appendChild(newScript)
        })

        // Update cache
        scriptCache.current = {content: gtmScript, isLoaded: true}
    }, [gtmScript])

    useEffect(() => {
        if (!gtmContext?._isReady && gtmEnabled) {
            try {
                if (typeof window !== 'undefined' && !window.dataLayer) {
                    loadGtmScript()
                    gtmContext.initialize()
                }
            } catch (error) {
                logger.error('GTM intialization', {
                    namespace: 'gtm-initalize',
                    additionalProperties: {error: error.message}
                })
            }
        }
    }, [gtmEnabled, gtmScript, gtmContext, loadGtmScript])

    const contextValue = useMemo(() => {
        if (gtmEnabled) {
            const currency = locale?.preferredCurrency
            const currencyCode = locale?.preferredCurrency
            const localeID = locale?.id?.replace('-', '_')
            const pageLanguage = locale?.id?.replace('-', '_')
            const userAgent = typeof window !== 'undefined' ? window?.navigator?.userAgent : ''
            const urlParams = new URLSearchParams(location.search)
            const scCid = urlParams.has('ScCid') ? sanitizeInput(urlParams.get('ScCid')) : ''
            const ttclid = urlParams.has('ttclid') ? sanitizeInput(urlParams.get('ttclid')) : ''
            const fbclid = urlParams.has('fbclid') ? sanitizeInput(urlParams.get('fbclid')) : ''

            return {
                currency,
                currencyCode,
                localeID,
                pageLanguage,
                email,
                registered,
                loggedInState,
                user_id,
                demandwareID,
                userAgent,
                scCid,
                ttclid,
                fbclid,
                appOrigin,
                environment
            }
        }
    }, [gtmEnabled, email])

    return <GTMContext.Provider value={contextValue}>{children}</GTMContext.Provider>
}

GTMProvider.propTypes = {
    children: PropTypes.node.isRequired,
    gtmContext: PropTypes.object
}
